// client/src/session/mapping.ts — `/users/me` → `Session` · AUTH-004.
//
// Pure: a wire shape in, the shell's type out, no store and no transport. Kept apart from
// `source.ts` so a test fixture can build a `Session` the same way the app does without
// pulling the api module in behind it — which is what a fixture used INSIDE `vi.mock('@/api/auth')`
// must not do, or the mock is evaluated on top of the module it replaces.

import type { CurrentUserResponse } from '@/api/auth';
import { wireNumber } from '@/lib/wire';

import type { RoleCode } from './catalog';
import { isPermission, isRoleCode } from './catalog';
import type { Session } from './types';

/** Throws on a role the vocabulary does not know — see `isRoleCode`. */
export function toSession(me: CurrentUserResponse): Session {
  if (!isRoleCode(me.role)) {
    throw new Error(`unknown role code from /users/me: ${me.role}`);
  }

  return {
    userId: me.userId,
    displayName: me.fullName,
    // One of the two is present (D-2710): an account a landlord created for a tenant with no
    // address has a user name instead, and the API omits whichever is absent.
    email: me.email ?? null,
    userName: me.userName ?? null,
    // `?? null` for the reason the two members below carry it: the API omits an absent member
    // rather than sending null (02 §7.4), and `Session` promises `string | null`.
    phone: me.phone ?? null,
    mfaEnabled: me.mfaEnabled,
    // `?? null`, on both of the nullable members, because the API does not send a null: it
    // omits the member (02 §7.4), so what arrives is `undefined` and every `=== null` test
    // downstream was false for the one state it existed to catch (D-1708). The `Session` type
    // promises `string | null`, and this is the boundary that makes the promise true.
    activeOrganizationId: me.activeOrganizationId ?? null,
    ownedPropertyCount: wireNumber(me.ownedPropertyCount),
    portalContractCount: wireNumber(me.portalContractCount),
    residentBuildingCount: wireNumber(me.residentBuildingCount),
    role: me.role,
    // Codes the client does not know are dropped, not thrown: a 37th permission shows up as
    // a menu entry nobody can see (catalog.ts) rather than as a login that fails.
    permissions: me.permissions.filter(isPermission),
    organizations: me.organizations.map((organization) => ({
      id: organization.id,
      name: organization.name,
      role: isRoleCode(organization.role) ? organization.role : 'tenant',
      isDefault: organization.isDefault,
      kind: organization.kind,
    })),
    activeClientId: me.activeClientId ?? null,
    // A client whose role the vocabulary does not know is dropped rather than coerced: unlike
    // an organization row, this one decides what the account may DO while it is chosen, and
    // offering it under a guessed role would be offering the wrong permissions (D-1699).
    clients: me.clients.filter((client) => isRoleCode(client.role)).map((client) => ({
      id: client.id,
      principalName: client.principalName,
      organizationId: client.organizationId,
      organizationName: client.organizationName,
      role: client.role as RoleCode,
    })),
    // Lane A — account deletion. Absent on the wire while nothing is scheduled (02 §7.4), so
    // `?? null` for the reason `activeClientId` carries it (D-1708, D-3003).
    deletionScheduledForUtc: me.deletionScheduledForUtc ?? null,
  };
}
